/* eslint-disable max-len */
import angular from 'angular';
import { COMPOSED_OF } from '~/scripts/constants/common-kids';
import Utils from '~/scripts/services/utils';
import { BaseController } from '~/utils/base-controller';
import APIV5 from '~/models/api';
import templateModalUsersCreation from '../modal/modal-users-creation-duplicate.html';
import ModalUsersCreationDuplicateController from '../modal/modal-users-creation-duplicate.controller';
import './new.scss';
import Lock from '~/scripts/services/model/lock';

/**
 * @typedef UserNewForm
 * @property {any[]} firstname
 * @property {any[]} civility
 * @property {any[]} email
 * @property {any[]} lastname
 * @property {any[]} roles
 * @property {any[]} orgs
 * @property {any[]} entreprise
 * @property {boolean} [isSuperAdmin=false]
 * @property {boolean} [notify=true]
 * @property {boolean} [loginPasswordChange=true]
 * @property {'K_NODE;ACC_REQ_STATUS_WAITING' | 'K_NODE;ACC_REQ_STATUS_READY' | 'K_NODE;ACC_REQ_STATUS_TODO'} [status='K_NODE;ACC_REQ_STATUS_TODO']
 */

/**
 * @typedef UserNewFormItem
 * @property {import('@knitiv/api-client-javascript').KNode} isa
 * @property {string} isa_name
 * @property {import('@knitiv/api-client-javascript').KNode} kid
 * @property {string} name
 * @property {boolean} orphan
 */

export const K_NODE_SUPERADMIN = 'K_NODE;SUPER_ADMIN';
// export const K_NODE_ACCREQSTATUS_EN_ATTENTE = 'K_NODE;ACC_REQ_STATUS_WAITING';
// export const K_NODE_ACCREQSTATUS_A_CREER = 'K_NODE;ACC_REQ_STATUS_TODO';
export const K_NODE_DATE_DEMANDE = 'K_NODE;DEMANDE';
export const K_NODE_NOTIFY = 'K_NODE;NOTIFY';
export const K_NODE_LOGIN_PASSWORD_CHANGE = 'K_NODE;LOGINPWDCHANGE'
export const K_NODE_BOOL = 'K_NODE;BOOL';
export const K_NODE_TRUE = 'K_NODE;TRUE';
export const K_NODE_FALSE = 'K_NODE;FALSE';
export const K_NODE_REQUEST_STATUS = 'K_NODE;ACCOUNT_REQUEST_STATUS';

export default class UsersNewController extends BaseController {
	/**
	 *
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {*} Notification
	 * @param {*} User
	 */

	/* @ngInject */
	constructor(
		$injector,
		$uibModal,
		Notification,
		User,
	) {
		super($injector);
		this.$uibModal = $uibModal;
		this.Notification = Notification;
		this.User = User;

		/** @type {boolean} */
		this.isAdmin = !!User.getProfil().admin;

		/** @type {UserNewForm} */
		this.form = {
			firstname:           [],
			civility:            [],
			email:               [],
			lastname:            [],
			roles:               [],
			orgs:                [],
			entreprise:          [],
			isSuperAdmin:        false,
			notify:              true,
			loginPasswordChange: true,
			status:              'K_NODE;ACC_REQ_STATUS_TODO',
		};

		/** @type {import('~/scripts/services/model/lock').default} */
		this.lockInfos = new Lock();

		/** @type {boolean} */
		this.isLoading = true;

		/** @type {any} */
		this.queryStrings = this.$location.search();

		/** @type {boolean} */
		this.isEditMode = !!this.queryStrings.edit;

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.editKid = this.isEditMode ? this.queryStrings.edit : null;

		/** @type {import('@knitiv/api-client-javascript').AuthenticationObject | undefined} */
		this.user = this.$rootScope.context;

		if (this.isEditMode) {
			// Retrieve infos and put them in the form
			(async () => this.init())();
		}
		else {
			this.isLoading = false;
		}
	}

	async backToUsers() {
		if (this.isEditMode && this.editKid) {
			await APIV5.instance.nodeUnlock(this.editKid, { id: 'users.new.back-to-users' });
		}
		this.$timeout(() => {
			this.$location.url('/users');
		});
	}

	async handleUser(isEditMode = false) {
		const {
			/** @type {UserNewFormItem[]} */ firstname,
			/** @type {UserNewFormItem[]} */ civility,
			/** @type {UserNewFormItem[]} */ email,
			/** @type {UserNewFormItem[]} */ lastname,
			/** @type {UserNewFormItem[]} */ roles,
			/** @type {UserNewFormItem[]} */ orgs,
			/** @type {UserNewFormItem[]} */ entreprise,
		} = this.form;

		if (
			firstname.length === 0
			// || civility.length === 0
			|| email.length === 0
			|| lastname.length === 0
			|| (roles.length === 0 && orgs.length === 0)
			|| entreprise.length === 0
		) {
			this.Notification.error({
				title: 'Erreur',
				message: 'Des champs sont manquants',
			});
			return;
		}

		if (email.length > 0 && !this.isEmail(email[0].name)) {
			this.Notification.error({
				title: 'Erreur',
				message: 'L\'email semble invalide !',
			});
			return;
		}

		/** @type {any[]} */
		const form = [
			firstname.map(element => ({
				...element,
				class_kid: 'K_NODE;FIRSTNAME',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
			civility.map(element => ({
				...element,
				class_kid: 'K_NODE;CIVILITY',
				lang_kid:  'K_NODE;FRANCAIS',
			})),
			email.map(element => ({
				...element,
				class_kid: 'K_NODE;EMAIL',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
			lastname.map(element => ({
				...element,
				class_kid: 'K_NODE;LASTNAME',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
			roles.map(element => ({
				...element,
				class_kid: 'K_NODE;KROLE',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
			orgs.map(element => ({
				...element,
				class_kid: 'K_NODE;ANYTHING',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
			entreprise.map(element => ({
				...element,
				class_kid: 'K_NODE;ENTREPRISE',
				lang_kid:  'K_NODE;UNIVERSEL',
			})),
		].flat(2);

		/** @type {import('@knitiv/api-client-javascript').ObjectCreateRepre[]} */
		const repre = [];

		/** @type {any[]} */
		const links = [];
		for (const field of [firstname, email, lastname]) {
			links.push(...field.map(f => ({
				class_kid: f.isa,
				value: f.name,
				setValueError: false,
			})));
		}

		if (!isEditMode) {
			const result = await APIV5.instance.objectSearchWithLinks({
				class_kid: 'K_NODE;PERSON_SYS',
				links,
				onKSYSTEM: true,
			}, {
				id: 'users-new.handle-user',
			});
			if (result?.result.total > 0) {
				/**
				 * @type {{
				 *   person: import('@knitiv/api-client-javascript').KNode | '__create' | undefined
				 *   profil: import('@knitiv/api-client-javascript').KNode | '__create' | undefined
				 * }}
				 */
				const modalResult = await this.$uibModal.open({
					ariaLabelledBy:  'modal-users-creation-title',
					ariaDescribedBy: 'modal-users-creation-body',
					template:        templateModalUsersCreation,
					controller:      ModalUsersCreationDuplicateController,
					controllerAs:    '$ctrl',
					resolve:         {
						links: () => result.result.list,
					},
				}).result;

				if (modalResult && modalResult.person && modalResult.person !== '__create') {
					repre.push({
						lang: 'K_NODE;PERSON_SYS',
						name: modalResult.person,
					});
					if (modalResult.profil && modalResult.profil !== '__create') {
						repre.push({
							lang: 'K_NODE;KPROFIL',
							name: modalResult.profil,
						});
					}
				}
			}
		}

		await (isEditMode
			? this.handleUserUpdate(form)
			: this.handleUserCreation(form, repre)
		);
	}

	/**
	 * @param {any[]} form
	 * @param {import('@knitiv/api-client-javascript').ObjectCreateRepre[]} repre
	 */
	async handleUserCreation(form, repre) {
		const assign = form.map(element => ({
			name: element.name,
			uid: this.User.getRefNr(),
		}));

		/** @type {any} */
		const linkList = form.map(/** @type {any} */ element => {
			const assoc = assign.find(a => a.name === element.name);
			if (!assoc) {
				throw new Error('No assoc found');
			}
			return ({
				objnum2:    element.kid ?? `K_NODE;${assoc.uid.toString()}`,
				class_kid:  element.class_kid,
				linker_kid: element.class_kid === 'K_NODE;ANYTHING' ? 'K_NODE;KDROITORG' : '0',
			});
		});

		// eslint-disable-next-line unicorn/no-array-reduce,unicorn/prefer-object-from-entries
		const repreList = form.reduce((/** @type {any} */ result, /** @type {any} */ element) => {
			const assoc = assign.find(a => a.name === element.name);
			if (!assoc) {
				throw new Error('No assoc found');
			}
			if (element.kid) {
				return result;
			}
			result[element.kid ?? `K_NODE;${assoc.uid.toString()}`] = {
				isa: element.isa,
				type_kid: 'K_NODE;STRING',
				REPRE_LIST: [
					{
						name: element.name,
						lang: element.class_kid,
					},
				],
			};
			return result;
		}, {});
		const isAdminAssoc = {
			Oui: K_NODE_TRUE, Non: K_NODE_FALSE,
		};

		linkList.push(
			{
				objnum2:    this.form.isSuperAdmin ? isAdminAssoc.Oui : isAdminAssoc.Non, // Super admin
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_SUPERADMIN,
			},
			{
				objnum2:    'K_NODE;_NR_DATE_DEMANDE', // date de demande
				class_kid:  'K_NODE;DATE',
				linker_kid: K_NODE_DATE_DEMANDE,
			},
			{
				objnum2:    this.form.notify ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_NOTIFY,
			},
			{
				objnum2:    this.form.loginPasswordChange ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // changer le mot de passe
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_LOGIN_PASSWORD_CHANGE,
			},
			{
				objnum2:    this.form.status, // statys
				class_kid:  K_NODE_REQUEST_STATUS,
				linker_kid: '0',
			},
		);

		/** @type {import('@knitiv/api-client-javascript').ObjectSetInput['items']} */
		const newItem = [
			{
				target_objnum: 'K_NODE;_NR_ACCOUNT_REQUEST',
				OBJECT_LIST: {
					'K_NODE;_NR_ACCOUNT_REQUEST': {
						isa: 'K_NODE;ACCOUNT_REQUEST',
						LINK_LIST: {
							[COMPOSED_OF]: linkList,
						},
						REPRE_LIST: repre,
					},
				},
			},
		];

		newItem[0].OBJECT_LIST = Object.assign(newItem[0].OBJECT_LIST, repreList);

		newItem[0].OBJECT_LIST['K_NODE;_NR_DATE_DEMANDE'] = { // Date demande
			isa: 'K_NODE;DATE', // @ts-ignore
			type_kid: 'K_NODE;DATE',
			REPRE_LIST: [
				{
					name: Utils.isoDate(), // Take current date
					lang: 'K_NODE;UNIVERSEL',
				},
			],
		};

		const result = await APIV5.instance.objectSet({
			items: newItem,
		}, { id: 'users.creation' });
		if (result) {
			this.$timeout(() => {
				this.Notification.success({
					title:   this.$translate.instant('object.successfully-created-title'),
					message: this.$translate.instant('object.successfully-created-message'),
				});
				this.$location.url('/users');
			});
		}
	}

	/**
  * @param {any} form
  * @returns {Promise<any>}
  */
	async handleUserUpdate(form) {
		const isAdminAssoc = {
			Oui: K_NODE_TRUE, Non: K_NODE_FALSE,
		};
		/**  @type {import('@knitiv/api-client-javascript').ObjectCreateLink[]} */
		const diff = [];
		const newValues = this.form;
		const oldValues = this.oldForm;

		let uniqNumber = 0;
		const assign = form.map((/** @type {any} */ element) => {
			uniqNumber += 1;
			return {
				class_kid: element.class_kid,
				name:      element.name,
				uid:       uniqNumber,
			};
		});

		for (const [key] of Object.entries(newValues)) {
			// @ts-ignore
			const newValue = newValues[key];
			// @ts-ignore
			const oldValue = oldValues[key];

			// if (oldVal.length === 0) return;

			if (Array.isArray(newValue)) {
				for (const entry of newValue) {
					const oldValueFound = oldValue.find((/** @type {any} */ oldEntry) => oldEntry.kid === entry.kid);
					if (!oldValueFound) {
						let classKid = entry.isa;
						if (key === 'roles') {
							classKid = 'K_NODE;KROLE';
						}
						else if (key === 'orgs') {
							classKid = 'K_NODE;ANYTHING';
						}
						const assoc = assign.find((/** @type {any} */ a) => a.name === entry.name);
						diff.push({
							action:     'ADD',
							objnum2:    entry.kid ?? `K_NODE;_NR_${assoc.uid.toString()
								.padStart(6, '0')}`,
							class_kid:  classKid,
							linker_kid: classKid === 'K_NODE;ANYTHING' ? 'K_NODE;KDROITORG' : '0',
						});
					}
				}

				for (const entry of oldValue) {
					const newValueFound = newValue.find((/** @type {any} */ oldEntry) => oldEntry.kid === entry.kid);
					if (!newValueFound) {
						let classKid = entry.isa;
						if (key === 'roles') {
							classKid = 'K_NODE;KROLE';
						}
						else if (key === 'orgs') {
							classKid = 'K_NODE;ANYTHING';
						}
						diff.push({
							action:     'DEL',
							objnum2:    entry.kid,
							class_kid:  classKid,
							linker_kid: classKid === 'K_NODE;ANYTHING' ? 'K_NODE;KDROITORG' : '0',
						});
					}
				}
			}
		}

		if (this.form.isSuperAdmin !== this.oldForm?.isSuperAdmin) {
			diff.push({
				action:     'DEL',
				objnum2:    this.oldForm?.isSuperAdmin ? isAdminAssoc.Oui : isAdminAssoc.Non, // Super admin
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_SUPERADMIN,
			}, {
				action:     'ADD',
				objnum2:    this.form.isSuperAdmin ? isAdminAssoc.Oui : isAdminAssoc.Non, // Super admin
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_SUPERADMIN,
			});
		}

		if (this.form.notify !== this.oldForm?.notify) {
			diff.push({
				action:     'DEL',
				objnum2:    this.oldForm?.notify ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_NOTIFY,
			},
			{
				action:     'ADD',
				objnum2:    this.form.notify ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_NOTIFY,
			});
		}

		if (this.form.loginPasswordChange !== this.oldForm?.loginPasswordChange) {
			diff.push({
				action:     'DEL',
				objnum2:    this.oldForm?.loginPasswordChange ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_LOGIN_PASSWORD_CHANGE,
			},
			{
				action:     'ADD',
				objnum2:    this.form.loginPasswordChange ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_LOGIN_PASSWORD_CHANGE,
			});
		}

		diff.sort((a /* , b */) => {
			if (a.action === 'DEL') {
				return -1;
			}
			return 1;
		});

		/** @type {import('@knitiv/api-client-javascript').ObjectCreateLink[]} */
		const linkList = form.map((/** @type {any} */ element) => {
			const assoc = assign.find((/** @type {any} */ a) => a.name === element.name);
			return ({
				objnum2:    element.kid ?? `K_NODE;_NR_${assoc.uid.toString()
					.padStart(6, '0')}`,
				class_kid:  element.isa,
				linker_kid: '0',
			});
		});

		// eslint-disable-next-line unicorn/no-array-reduce,unicorn/prefer-object-from-entries
		const repreList = form.reduce((/** @type {any} */ result, /** @type {any} */ element) => {
			if (element.kid) {
				return result;
			}
			const assoc = assign.find((/** @type {any} */ a) => a.name === element.name);
			result[element.kid ?? `K_NODE;_NR_${assoc.uid.toString()
				.padStart(6, '0')}`] = {
				isa:        element.isa,
				type_kid:   'K_NODE;STRING',
				REPRE_LIST: [
					{
						name: element.name,
						lang: element.lang_kid,
					},
				],
			};
			return result;
		}, {});

		linkList.push(
			{
				objnum2:    form.isSuperAdmin ? isAdminAssoc.Oui : isAdminAssoc.Non, // Super admin
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_SUPERADMIN,
			},
			{
				objnum2:    this.form.notify ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // date de demande
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_NOTIFY,
			},
			{
				objnum2:    this.form.loginPasswordChange ? 'K_NODE;TRUE' : 'K_NODE;FALSE', // changer le mot de passe
				class_kid:  K_NODE_BOOL,
				linker_kid: K_NODE_LOGIN_PASSWORD_CHANGE,
			},
		);

		if (this.editKid) {
			/** @type {import('@knitiv/api-client-javascript').ObjectCreateItem[]} */
			const items = [
				{
					target_objnum: this.editKid,
					OBJECT_LIST:   {
						[this.editKid]: {
							isa:       'K_NODE;ACCOUNT_REQUEST',
							LINK_LIST: {
								[COMPOSED_OF]: diff,
							},
						},
					},
				},
			];

			items[0].OBJECT_LIST = Object.assign(items[0].OBJECT_LIST, repreList);

			try {
				await APIV5.instance.objectModify({ items }, { id: 'users.new.update' });
				this.Notification.success({
					title:   this.$translate.instant('object.successfully-created-title'),
					message: this.$translate.instant('object.successfully-created-message'),
				});
				await APIV5.instance.nodeUnlock(this.editKid, { id: 'users.new.created' });
			}
			catch (error) {
				console.error(error);
			}
			finally {
				this.$timeout(() => {
					this.$location.url('/users');
				});
			}
		}
	}

	async init() {
		if (this.editKid) {
			const data = await APIV5.instance.objectInfo(
				{ kid: this.editKid },
				{ id: 'users.new.init' },
			);
			// If locked, just return
			if (data?.lock.locked && data.lock.profil.kid !== this.user?.profil.kid && data.lock.date) {
				this.lockInfos = new Lock(data.lock);
				this.isLoading = false;
			}

			if (data && this.editKid) {
				await APIV5.instance.nodeLock(this.editKid, { id: 'users.new.init' });
				// Otherwise
				this.elements = data?.link.list.from[COMPOSED_OF]?.list;

				if (this.elements) {
					const civilityEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;CIVILITY');
					const emailEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;EMAIL');
					const firstnameEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;FIRSTNAME');
					const lastnameEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;LASTNAME');
					const rolesEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;KROLE');
					const orgsEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;ANYTHING' && element.linker_kid === 'K_NODE;KDROITORG');
					const entrepriseEls = Object.values(this.elements)
						.filter(element => element.class_kid === 'K_NODE;ENTREPRISE');
					const superAdminEls = Object.values(this.elements)
						.find(element => element.class_kid === K_NODE_BOOL && element?.linker_kid === K_NODE_SUPERADMIN);
					const notify = Object.values(this.elements)
						.find(element => element.class_kid === K_NODE_BOOL && element?.linker_kid === K_NODE_NOTIFY);
					const loginPasswordChange = Object.values(this.elements)
						.find(element => element.class_kid === K_NODE_BOOL && element?.linker_kid === K_NODE_LOGIN_PASSWORD_CHANGE);

					this.form.isSuperAdmin = superAdminEls?.objname === 'Oui';
					this.form.notify = notify?.objnum2_kid === 'K_NODE;TRUE' ?? false;
					this.form.loginPasswordChange = loginPasswordChange?.objnum2_kid === 'K_NODE;TRUE' ?? false;

					// eslint-disable-next-line unicorn/consistent-function-scoping
					const toMf = (/** @type {any} */ element) => ({
						isa:      element.isa,
						isa_name: element.isaname,
						kid:      element.objnum2_kid,
						name:     element.objname,
					});

					this.form.firstname = firstnameEls.map(element => toMf(element));
					this.form.civility = civilityEls.map(element => toMf(element));
					this.form.email = emailEls.map(element => toMf(element));
					this.form.lastname = lastnameEls.map(element => toMf(element));
					this.form.roles = rolesEls.map(element => toMf(element));
					this.form.orgs = orgsEls.map(element => toMf(element));
					this.form.entreprise = entrepriseEls.map(element => toMf(element));

					this.oldForm = angular.copy(this.form);
				}
			}
		}

		this.$timeout(() => {
			this.isLoading = false;
		});
	}

	/**
  * Check if string is email address
  *
  * @param {string} email
  * @returns {boolean} true if email address is valid
  */
	isEmail(email) {
		const r = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Za-z-]+\.)+[A-Za-z]{2,}))$/;
		return r.test(email);
	}
}
